/**
 * Add User wizard.
 *
 * CR-5313: rebuilt as a four-step flow so admins can:
 *   1. Capture the user profile up front (title, name, email, phone, job title, dept).
 *   2. Pick the primary company + any additional sites in the same group.
 *   3. Set per-site access (modules, notifications, read-only override), with an
 *      "apply same settings to every site" toggle that drives bulk apply.
 *   4. Review the summary and send the invite.
 *
 * The legacy flow only supported one company and exposed the Compliance
 * module nowhere. This wizard layers on top of the existing
 * `/core/invitations/` endpoint plus the per_site_permissions JSON map
 * the backend now honours, so we don't need a UserProfile migration to
 * support multi-site assignments within a customer/supplier group.
 *
 * 2026-10-14: on a customer invite, picking a department pre-fills the three
 * notification switches (Procurement / Quality / Documents) from
 * `lib/departments`; the admin can still change them in step 3. Steps live in
 * `./invite-user/` to keep this file under 500 lines.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  invitationAPI,
  coreAPI,
  type UserInvitationCreate,
  type UserSitePermissionFlags,
  type Role,
} from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  UserPlus, ArrowLeft, ArrowRight, AlertCircle, CheckCircle2, Building2, Truck,
} from 'lucide-react';
import {
  STEPS, DEFAULT_PERMISSION_FLAGS, EMPTY_PROFILE, withDepartmentNotifyDefaults,
  refillDepartmentNotifyDefaults,
  type WizardStep, type PermissionFlags, type ProfileFields,
} from './invite-user/constants';
import { useInviteCompanies } from './invite-user/useInviteCompanies';
import { ProfileStep } from './invite-user/ProfileStep';
import { CompaniesStep } from './invite-user/CompaniesStep';
import { AccessStep, ReviewStep } from './invite-user/AccessReviewSteps';

export default function InviteUser() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteType = (searchParams.get('type') || 'customer') as 'customer' | 'supplier';
  const isSupplierInvite = inviteType === 'supplier';

  const [step, setStep] = useState<WizardStep>(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1
  const [profile, setProfile] = useState<ProfileFields>(EMPTY_PROFILE);

  // Step 2
  const sites = useInviteCompanies(isSupplierInvite, user?.company, setError);
  const {
    loadingOptions, companies, primaryCompanyId, additionalSiteIds,
    primarySelection, sitesUnderGroup, primaryCompany, selectedSiteIds,
  } = sites;

  // Step 3
  const [roles, setRoles] = useState<Role[]>([]);
  // A customer invite starts from the "no department" defaults (all
  // notifications off) so it matches picking "—"; the admin sees and can
  // change every switch in step 3.
  const [defaultFlags, setDefaultFlags] = useState<PermissionFlags>(
    () => (isSupplierInvite
      ? DEFAULT_PERMISSION_FLAGS
      : withDepartmentNotifyDefaults(DEFAULT_PERMISSION_FLAGS, '')),
  );
  // CR-5313: when true the matrix shows one shared row of toggles; when
  // false each selected site gets its own override card. Maps to the
  // backend's per_site_permissions JSON.
  const [applyToAll, setApplyToAll] = useState(true);
  const [perSiteOverrides, setPerSiteOverrides] = useState<Record<number, PermissionFlags>>({});

  const TypeIcon = isSupplierInvite ? Truck : Building2;

  useEffect(() => {
    if (primaryCompanyId) void loadRoles();
  }, [primaryCompanyId, inviteType]);

  // CR-5313: when the user toggles "apply same settings" off, seed each
  // selected site's overrides from the current defaults so they have a
  // sensible starting point instead of an empty matrix.
  useEffect(() => {
    if (applyToAll) return;
    const allIds = [primaryCompanyId, ...Array.from(additionalSiteIds)].filter(Boolean);
    setPerSiteOverrides(prev => {
      const next: Record<number, PermissionFlags> = {};
      for (const id of allIds) {
        next[id] = prev[id] ?? { ...defaultFlags };
      }
      return next;
    });
  }, [applyToAll, primaryCompanyId, additionalSiteIds]);

  const loadRoles = async () => {
    try {
      const companyType = isSupplierInvite ? 'supplier' : 'customer';
      const res = await coreAPI.getRoles({ company_type: companyType, is_active: true });
      setRoles(res.data.results || []);
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  // Customer invites: a department pick moves the three notification
  // switches — shared defaults and every per-site override — from the old
  // department's defaults to the new one's (lib/departments). A switch the
  // admin already changed by hand is left as they set it. Supplier invites
  // keep the standard defaults.
  const handleDepartmentChange = (department: string) => {
    const from = profile.department;
    setProfile(prev => ({ ...prev, department }));
    if (isSupplierInvite) return;
    setDefaultFlags(prev => refillDepartmentNotifyDefaults(prev, from, department));
    setPerSiteOverrides(prev => {
      const next: Record<number, PermissionFlags> = {};
      for (const [id, flags] of Object.entries(prev)) {
        next[Number(id)] = refillDepartmentNotifyDefaults(flags, from, department);
      }
      return next;
    });
  };

  const flagsForSite = (siteId: number): PermissionFlags => {
    if (applyToAll) return defaultFlags;
    return perSiteOverrides[siteId] ?? defaultFlags;
  };

  const setFlag = (siteId: number | null, key: keyof UserSitePermissionFlags, value: boolean) => {
    if (siteId === null || applyToAll) {
      setDefaultFlags(prev => ({ ...prev, [key]: value }));
      return;
    }
    setPerSiteOverrides(prev => ({
      ...prev,
      [siteId]: { ...(prev[siteId] ?? defaultFlags), [key]: value },
    }));
  };

  const validateStep = (target: WizardStep): string | null => {
    if (target >= 2 || step >= 1) {
      if (!profile.first_name.trim()) return 'First name is required.';
      if (!profile.last_name.trim()) return 'Last name is required.';
      if (!profile.email.trim()) return 'Email is required.';
    }
    if (target >= 3 || step >= 2) {
      if (!primaryCompanyId) return 'Pick a primary company.';
      // CR-7076: role is required — a conscious decision, never a default.
      if (!profile.role) return 'Pick a user role. Every user needs one.';
    }
    return null;
  };

  const goTo = (target: WizardStep) => {
    setError(null);
    if (target > step) {
      const reason = validateStep(target);
      if (reason) {
        setError(reason);
        return;
      }
    }
    setStep(target);
  };

  const handleSubmit = async () => {
    setError(null);
    const reason = validateStep(4);
    if (reason) { setError(reason); return; }
    setSubmitting(true);

    // CR-5313: per_site_permissions only carries the rows where the
    // admin actually overrode the defaults — keeps the JSON tidy and
    // makes the backend's "fall back to invitation defaults" branch
    // the common case.
    const overridesPayload: Record<string, UserSitePermissionFlags> = {};
    if (!applyToAll) {
      for (const sid of selectedSiteIds) {
        const flags = perSiteOverrides[sid];
        if (!flags) continue;
        const diff: UserSitePermissionFlags = {};
        let dirty = false;
        (Object.keys(DEFAULT_PERMISSION_FLAGS) as (keyof UserSitePermissionFlags)[]).forEach(k => {
          if (flags[k] !== defaultFlags[k]) {
            (diff as Record<string, boolean>)[k] = flags[k] as boolean;
            dirty = true;
          }
        });
        if (dirty) overridesPayload[String(sid)] = diff;
      }
    }

    const pickedGroupId = primarySelection.startsWith('group:')
      ? parseInt(primarySelection.split(':')[1])
      : 0;
    const invitedGroupId = pickedGroupId
      && sitesUnderGroup(pickedGroupId).every(
        c => c.id === primaryCompanyId || additionalSiteIds.has(c.id),
      )
      ? pickedGroupId
      : undefined;

    const payload: UserInvitationCreate = {
      email: profile.email.trim(),
      first_name: profile.first_name.trim(),
      last_name: profile.last_name.trim(),
      salutation: profile.salutation || undefined,
      phone: profile.phone.trim() || undefined,
      company: primaryCompanyId,
      // CR-7425: keep the Group / Division pick, so the invite email and
      // accept page name it instead of the first site it expands to.
      // Only while every site beneath it is still ticked: an admin who
      // unticks sites is making a site invite.
      invited_group: invitedGroupId,
      role: profile.role,
      job_title: profile.job_title || undefined,
      department: profile.department || undefined,
      message: profile.message || undefined,
      site_ids: Array.from(additionalSiteIds),
      ...defaultFlags,
      per_site_permissions: applyToAll ? undefined : overridesPayload,
    };

    try {
      await invitationAPI.createInvitation(payload);
      setSuccess(true);
      setTimeout(() => navigate(`/admin/users?tab=${inviteType}`), 1800);
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: {
          data?: {
            email?: string[]; error?: string; detail?: string;
            // Platform error envelope: field errors live under
            // details.fields — without reading it, a validation refusal
            // (e.g. the allowed-email-domain policy) surfaced as the
            // generic "try again", hiding the actionable reason.
            details?: { fields?: Record<string, string[]> };
          };
        };
      };
      const fields = axiosErr.response?.data?.details?.fields;
      const firstFieldError = fields
        ? Object.values(fields).flat()[0]
        : undefined;
      setError(
        firstFieldError ||
        axiosErr.response?.data?.email?.[0] ||
        axiosErr.response?.data?.error ||
        axiosErr.response?.data?.detail ||
        'Failed to send invitation. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingOptions) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <Button variant="ghost" onClick={() => navigate(`/admin/users?tab=${inviteType}`)} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />Back to User Management
      </Button>

      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-primary/10 rounded-lg">
          <UserPlus className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <TypeIcon className="h-7 w-7" />
            Add {isSupplierInvite ? 'Supplier' : 'Customer'} User
          </h1>
          <p className="text-muted-foreground">
            Profile first, then companies, then per-site access.
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center mb-6">
        {STEPS.map((s, idx) => {
          const Icon = s.icon;
          const isCurrent = step === s.num;
          const isDone = step > s.num;
          return (
            <div key={s.num} className="flex items-center flex-1">
              <button
                type="button"
                onClick={() => { if (s.num < step) goTo(s.num); }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  isCurrent ? 'bg-primary text-primary-foreground font-semibold'
                    : isDone ? 'text-primary hover:bg-primary/10 cursor-pointer'
                      : 'text-muted-foreground'
                }`}
                disabled={s.num > step}
              >
                <Icon className="h-4 w-4" />
                <span>{s.num}. {s.label}</span>
              </button>
              {idx < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 ${step > s.num ? 'bg-primary' : 'bg-border'}`} />
              )}
            </div>
          );
        })}
      </div>

      {success && (
        <Alert className="mb-4 border-green-500 bg-green-50 dark:bg-green-950">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            Invitation sent successfully. Redirecting…
          </AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {step === 1 && (
        <ProfileStep
          profile={profile}
          onChange={setProfile}
          onDepartmentChange={handleDepartmentChange}
          departmentHint={isSupplierInvite
            ? undefined
            : 'Sets the starting notification switches. You can change them in step 3.'}
        />
      )}

      {step === 2 && (
        <CompaniesStep
          sites={sites}
          TypeIcon={TypeIcon}
          roles={roles}
          roleId={profile.role}
          onRoleChange={role => setProfile({ ...profile, role })}
        />
      )}

      {step === 3 && (
        <AccessStep
          applyToAll={applyToAll}
          onApplyToAllChange={setApplyToAll}
          defaultFlags={defaultFlags}
          selectedSiteIds={selectedSiteIds}
          primaryCompanyId={primaryCompanyId}
          companies={companies}
          flagsForSite={flagsForSite}
          setFlag={setFlag}
          showDocuments={!isSupplierInvite}
        />
      )}

      {step === 4 && (
        <ReviewStep
          profile={profile}
          primaryCompany={primaryCompany}
          additionalSiteIds={additionalSiteIds}
          companies={companies}
          roles={roles}
          applyToAll={applyToAll}
          defaultFlags={defaultFlags}
          selectedSiteIds={selectedSiteIds}
          flagsForSite={flagsForSite}
          showDocuments={!isSupplierInvite}
        />
      )}

      {/* Step nav */}
      <div className="flex items-center justify-between mt-6">
        <Button variant="outline" disabled={step === 1 || submitting}
          onClick={() => goTo((step - 1) as WizardStep)}>
          <ArrowLeft className="h-4 w-4 mr-2" />Back
        </Button>
        {step < 4 ? (
          <Button onClick={() => goTo((step + 1) as WizardStep)}>
            Next<ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={submitting || success}>
            {submitting ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Sending…</>
            ) : (
              <><UserPlus className="h-4 w-4 mr-2" />Send invitation</>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
